import { Heart } from '@phosphor-icons/react';
import classNames from 'classnames';
import type { CSSProperties } from 'styled-components';

import { getBackgroundColorStyle } from '../utils/background';
import { computeColor } from '../utils/colors';
import { computeFont } from '../utils/fonts';
import { getFontSizeVariables } from '../utils/getFontSizeVariables';

import * as styles from './Navbar.css';
import type { NavbarMenuDropdownItemElementProps } from './Navbar.type';
import { NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS } from './NavbarMenuDropdownItem.constants';
import { renderItemIcon } from './renderItemIcon';

export function NavbarMenuDropdownItem(props: NavbarMenuDropdownItemElementProps) {
  const { element, className, onClick } = props;
  const {
    style,
    padding,
    background,
    borderRadius,
    customAttributes,
    title,
    titleFontSize,
    titleFontWeight,
    titleLetterSpacing,
    description,
    descriptionFontSize,
    descriptionFontWeight,
    descriptionLetterSpacing,
    icon,
    iconColor,
    iconBackgroundColor,
    href,
    new_tab,
    color,
    fontFamily,
    descriptionFontFamily,
    descriptionColor,

    hoverBorderColor,
    hoverBorderStyle,
    hoverBorderWidth,
    hoverBorderRadius,
    hoverTextColor,
    hoverDescriptionTextColor,
    hoverIconColor,
    hoverIconBackgroundColor,
    hoverBackgroundColor,
  } = element.attrs || {};

  const DEFAULTS = NAVBAR_MENU_DROPDOWN_ITEM_DEFAULTS;

  const visible = element.attrs?.visible ?? true;

  if (!visible) {
    return null;
  }

  return (
    <a
      {...props}
      {...customAttributes}
      className={classNames(
        style === 'featured' ? styles.navbarDropdownItemFeatured : styles.navbarDropdownItem,
        className,
        customAttributes?.className,
      )}
      onClick={(e) => {
        if (onClick) {
          e?.preventDefault();
          onClick();
        }
      }}
      style={
        {
          '--padding': padding ?? DEFAULTS.padding,
          '--border-radius': borderRadius ?? DEFAULTS.borderRadius,
          '--background-color': getBackgroundColorStyle(background ?? DEFAULTS.background).background,
          '--text-color': computeColor(color ?? DEFAULTS.textColor),
          '--icon-color': computeColor(iconColor ?? DEFAULTS.iconColor),
          '--icon-background-color': computeColor(iconBackgroundColor ?? DEFAULTS.iconBackgroundColor),
          '--font-family': computeFont(fontFamily) || DEFAULTS.fontFamily,
          ...getFontSizeVariables(titleFontSize ?? DEFAULTS.titleFontSize, undefined, 'title'),
          '--title-font-weight': titleFontWeight ?? DEFAULTS.titleFontWeight,
          ...getFontSizeVariables(descriptionFontSize ?? DEFAULTS.descriptionFontSize, undefined, 'description'),
          '--title-letter-spacing': titleLetterSpacing ?? DEFAULTS.titleLetterSpacing,
          '--description-letter-spacing': descriptionLetterSpacing ?? DEFAULTS.descriptionLetterSpacing,
          '--description-font-weight': descriptionFontWeight ?? DEFAULTS.descriptionFontWeight,
          '--description-text-color': computeColor(descriptionColor ?? DEFAULTS.descriptionTextColor),
          '--description-font-family': computeFont(descriptionFontFamily) || DEFAULTS.fontFamily,

          '--hover-border-radius': hoverBorderRadius || borderRadius || DEFAULTS.borderRadius,
          '--hover-text-color': hoverTextColor || color || DEFAULTS.textColor,
          '--hover-description-text-color':
            hoverDescriptionTextColor || descriptionColor || DEFAULTS.descriptionTextColor,
          '--hover-background-color': hoverBackgroundColor || background || DEFAULTS.hoverBackgroundColor,
          '--hover-icon-color': hoverIconColor || iconColor || DEFAULTS.iconColor,
          '--hover-icon-background-color':
            hoverIconBackgroundColor || iconBackgroundColor || DEFAULTS.iconBackgroundColor,
          '--hover-border-color': hoverBorderColor || DEFAULTS.borderColor,
          '--hover-border-style': hoverBorderStyle || DEFAULTS.borderStyle,
          '--hover-border-width': hoverBorderWidth || DEFAULTS.borderWidth,
        } as CSSProperties
      }
      href={href}
      target={new_tab ? '_blank' : '_self'}
    >
      {['icon', 'featured'].includes(style || '') && (
        <div className={styles.navbarDropdownIcon}>
          {icon ? (
            renderItemIcon({ icon, color: iconColor })
          ) : (
            <Heart size={24} weight="fill" className="text-wb-secondary" />
          )}
        </div>
      )}
      <div className={styles.navbarDropdownItemTexts}>
        <p className={styles.navbarDropdownItemTitle}>{title}</p>
        {['description', 'icon', 'featured'].includes(style || '') && (
          <p className={styles.navbarDropdownItemDescription}>{description}</p>
        )}
      </div>
    </a>
  );
}
